// Pure helpers for the clinic-location calendar page (MentorClinicBookingsPage):
//  - slug-or-id URL key resolution, so /mentor-home/my-clinic-associations/<locationSlug>
//    works alongside the legacy /<clinicBrandId>/<clinicLocationId> form, and
//  - location-scoped check-in / peek-in access derivation from the localStorage keys
//    (checkedInDetails + peekedInRoomDetails) that the check-in flows maintain.
// Kept free of React/MUI imports so jest can exercise them directly.

/**
 * Clinic-location ids in production carry one of these prefixes (NST- is the historical
 * KeyGen default, CLN- the newer explicit prefix, clinic- the oldest hand-made ids).
 * Used only as a fallback classifier when a URL key isn't found in the loaded
 * associations — a prefixed key is still treated as an id so deep links to locations
 * outside the practitioner's association list keep their id semantics.
 */
export function looksLikeClinicLocationId(urlKey) {
  return typeof urlKey === 'string' && /^(NST-|CLN-|clinic-)/i.test(urlKey)
}

/**
 * Resolve a URL key (location id or location slug) against the practitioner's clinic
 * associations. Id match wins over slug match so a legacy id that happens to collide
 * with some slug never changes meaning. Slug matching is case-insensitive because
 * stored slugs are mixed-case (e.g. "VNPC-Indira-Nagar") while hand-typed URLs tend
 * to be lowercase.
 */
export function findClinicAssociationByUrlKey(associations, urlKey) {
  if (!Array.isArray(associations) || !urlKey) {
    return undefined
  }

  const idMatch = associations.find((location) => location?.id === urlKey)
  if (idMatch) {
    return idMatch
  }

  const normalizedKey = String(urlKey).toLowerCase()
  return associations.find(
    (location) =>
      typeof location?.slug === 'string' &&
      location.slug.trim().toLowerCase() === normalizedKey,
  )
}

/**
 * Canonical path for a clinic location's calendar page. Prefers the short slug form
 * (location slugs are globally unique — enforced by the server's SlugService at
 * creation time — so the brand segment is redundant); falls back to the legacy
 * /<brandId>/<locationId> form when no slug is available.
 */
export function getClinicCalendarPath(clinicLocationData) {
  const slug =
    typeof clinicLocationData?.slug === 'string' ? clinicLocationData.slug.trim() : ''
  if (slug) {
    return `/mentor-home/my-clinic-associations/${encodeURIComponent(slug)}`
  }
  if (clinicLocationData?.clinicBrandId && clinicLocationData?.id) {
    return `/mentor-home/my-clinic-associations/${clinicLocationData.clinicBrandId}/${clinicLocationData.id}`
  }
  return '/mentor-home/my-clinic-associations'
}

/**
 * Whether a stored checkedInDetails record represents a check-in at the given clinic
 * location. Records missing clinicLocationId (older writes) are treated as matching
 * any location — same permissive behavior the page had before location scoping.
 */
export function isCheckedInAtClinicLocation(checkedInDetails, clinicLocationId) {
  if (!checkedInDetails?.chamberRoomId) {
    return false
  }
  const storedLocationId = checkedInDetails?.clinicLocationId
  if (!storedLocationId) {
    return true
  }
  return storedLocationId === clinicLocationId
}

/**
 * Find the active (non-expired) peek-in for a clinic location inside the stored
 * peekedInRoomDetails value, which is either a single room object (written by the
 * check-in/peek flows) or an array of peek entries with expiresAt (peekInManager).
 */
export function getActivePeekInAtClinicLocation(storedPeekData, clinicLocationId, nowMs = Date.now()) {
  if (!storedPeekData || !clinicLocationId) {
    return null
  }

  const entries = Array.isArray(storedPeekData) ? storedPeekData : [storedPeekData]

  return (
    entries.find((peekIn) => {
      if (!peekIn?.id) {
        return false
      }
      // Entries missing clinicLocationId (older writes) match any location — same
      // permissive rule as check-ins, so a stored session never gets invalidated by a
      // build/reload just because it predates location scoping.
      if (peekIn?.clinicLocationId && peekIn.clinicLocationId !== clinicLocationId) {
        return false
      }
      if (!peekIn.expiresAt) {
        return true
      }
      const expiresAt = Date.parse(peekIn.expiresAt)
      return Number.isFinite(expiresAt) && expiresAt > nowMs
    }) || null
  )
}

function parseStoredJson(value) {
  if (!value) {
    return null
  }
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

/**
 * Derive the practitioner's access state for one clinic location from the raw stored
 * strings. Returns the clinicLocationId it was derived for so consumers can ignore a
 * result computed for a stale location (route transitions).
 */
export function deriveClinicLocationAccess(
  { checkedInRaw = null, peekRaw = null } = {},
  clinicLocationId,
  nowMs = Date.now(),
) {
  const checkedInDetails = parseStoredJson(checkedInRaw)
  const storedPeekData = parseStoredJson(peekRaw)
  const activePeekIn = getActivePeekInAtClinicLocation(storedPeekData, clinicLocationId, nowMs)

  return {
    clinicLocationId,
    isCheckedInHere: isCheckedInAtClinicLocation(checkedInDetails, clinicLocationId),
    isPeekedInHere: Boolean(activePeekIn),
    checkedInDetails,
    activePeekIn,
    checkedInRaw,
    peekRaw,
  }
}

export function clinicLocationAccessEquals(left, right) {
  return (
    left?.clinicLocationId === right?.clinicLocationId &&
    left?.isCheckedInHere === right?.isCheckedInHere &&
    left?.isPeekedInHere === right?.isPeekedInHere &&
    left?.checkedInRaw === right?.checkedInRaw &&
    left?.peekRaw === right?.peekRaw
  )
}

/** Read the raw localStorage strings the check-in flows maintain. */
export function readStoredCheckInAccessRaw() {
  try {
    return {
      checkedInRaw: localStorage.getItem('checkedInDetails'),
      peekRaw: localStorage.getItem('peekedInRoomDetails'),
    }
  } catch {
    return { checkedInRaw: null, peekRaw: null }
  }
}
